import styled from "styled-components";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { logError, LogCategory } from "utils/sentryLogger";
import { CloseIcon } from "../shared/ActionBar/icons/CloseIcon";
import { Button, Modal } from "react-bootstrap";
import { signOut } from "firebase/auth";
import { auth } from "hooks/useFirebase";
import { request } from "hooks/useOkoApi/useOkoApi";
import { useDataTrack } from "hooks/livekit/useDataTrack/useDataTrack";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";
import { ActionBar } from "../shared/ActionBar/ActionBar";
import { NotFound } from "./activities/NotFound/NotFound";
import { useSessionActivity } from "hooks/useSessionActivity/useSessionActivity";
import { SessionInspector } from "../SessionInspector";
import { MessageBar } from './MessageBar';
import { NetworkMonitor } from "./NetworkMonitor";
import { colors, constants } from "utils";
export const Activity = ({ disable }) => {
  const { activity, isShowSessionInspector, toggleSessionInspector, session } = useSessionActivity();
  const [isSuperUser, setIsSuperUser] = useState(true);
  const [message, setMessage] = useState("");
  
  // Determine the report location based on the current activity ID
  const getReportLocation = () => {
    const locationMap = {
      "ready-room": "Waiting Room",
      "waiting-room": "Waiting Room",
      "meet": "Crazy Calculator Start",
      "calculator": "Session",
      "multiple-choice": "Session",
      "empty": "Session",
    };
    
    return locationMap[activity?.id] || "Unknown";
  };
  
  useEffect(async () => {
    await request(`account/is_super_user`, { method: "GET" })
      .then((response) => setIsSuperUser(response))
      .catch((e) => {
        console.log(e);
        logError("Failed to check super user status", e, {
          category: LogCategory.SESSION,
        });
      });
  }, []);

  const Activity = activity?.component;
  let activityStyle = {};
  if (activity?.id === "calculator") {
    activityStyle = {...activityStyle, display: "contents" };
  }

  const updateMessage = (newMessage) => {
    setMessage(newMessage);
  };

  return (
    <Container
      className="fullscreen"
      style={{
        overflowY: "hidden",
        pointerEvents: disable ? "none" : "auto",
      }}
    >
      <div style={activityStyle}>
        {Activity ? <Activity /> : <NotFound />}
      </div>
      <MessageBar message={message} />
      <RightPanel>
        <Leave session={session} />
        <ActionBar
          reportLocation={getReportLocation()}
        />
      </RightPanel>
      <NetworkMonitor style={{ position: "absolute", bottom: "10px", right: "10px" }} updateMessage={updateMessage} />
      {isSuperUser && isShowSessionInspector && <SessionInspector toggleSessionInspector={toggleSessionInspector} />}
    </Container>
  );
};

const Container = styled.div`
  background: #edf2f9;
  display: flex;
  position: relative;
`;

const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  top: 1.5rem;
  right: 24px;
  gap: 12px;
  /* The session controls must never paint underneath activity content (the
     ready-room avatar cards are positioned with a z-index of their own). */
  z-index: 2;

  /* On tablets the browser chrome eats into the top of the viewport, leaving
     the close button partly cut off — start the rail a little lower. */
  @media ${constants.TABLET_MEDIA_QUERY} {
    top: 40px;
  }
`;

const LeaveButton = styled(Button)`
  width: 48px;
  height: 48px;
  padding: 0 !important;
  border: 2px ${colors.nightlyWoods950} solid !important;
  border-radius: 100px !important;
  color: ${colors.nightlyWoods950} !important;
  background: transparent !important;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Leave = ({ session }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSessionEnd, setIsSessionEnd] = useState(false);
  const [isSurveyDisplayFlagUpdated, setIsSurveyDisplayFlagUpdated] = useState(false);
  const { flushConnectionEventsSummary } = useVideoContext();

  // Track if we've already handled the session end to prevent duplicate calls
  const hasHandledSessionEnd = useRef(false);
  const hasCalledSurveyApi = useRef(false);

  const handleDataTrackMessage = useCallback(async (e) => {
    if (e.event_type === "state_change" && e.data?.state === "complete") {
      if (hasHandledSessionEnd.current) return;
      hasHandledSessionEnd.current = true;

      setIsSessionEnd(true);
      // Flush accumulated LiveKit connection events to Sentry
      flushConnectionEventsSummary?.();

      if (!hasCalledSurveyApi.current && session?.id) {
        hasCalledSurveyApi.current = true;
        await request(`feedback/${session?.id}/survey_displayed`, {})
          .catch((e) => {
            console.log(e);
            logError("Failed to mark survey as displayed", e, {
              category: LogCategory.SESSION,
              sessionId: session?.id,
            });
          });
      }
      setTimeout(() => onLeave(), 300000);
    }

    if (e.event_type === "inactivity_timeout") {
      console.log("[Session] inactivity_timeout received", { sessionId: session?.id, eventData: e });
      if (hasHandledSessionEnd.current) return;
      hasHandledSessionEnd.current = true;

      setIsSessionEnd(true);
      // Flush accumulated LiveKit connection events to Sentry
      flushConnectionEventsSummary?.();
      onLeave();
    }
  }, [flushConnectionEventsSummary, session?.id]);

  useDataTrack(handleDataTrackMessage);

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const onLeave = async () => {
    await signOut(auth);
    window.location.href = "https://okolabs.ai/";
  };

  const modalHeader = isSessionEnd ? "Session Survey" : "Exit Session?";
  const exitButtonText = isSessionEnd ? "Submit Survey" : "Exit";

  return (
    <>
      <LeaveButton
        aria-label="Leave session"
        onClick={ async () => {
          if (isSessionEnd) {
            // For session end, show the ConsensusTransitionOkoAssistant survey
            return;
          }
          setIsModalOpen(true);
          if (!isSurveyDisplayFlagUpdated && session?.id) {
            await request(`feedback/${session.id}/survey_displayed`, {})
              .then(() => setIsSurveyDisplayFlagUpdated(true))
              .catch((e) => {
                console.log(e);
                logError("Failed to update survey display flag", e, {
                  category: LogCategory.SESSION,
                  sessionId: session?.id,
                });
              });
          }
        }}
      >
        <CloseIcon />
      </LeaveButton>

      {/* Show regular modal for exit confirmation (when not session end) */}
      <Modal show={isModalOpen && !isSessionEnd}>
        <Modal.Header><span style={{ fontSize: "20px" }}>{modalHeader}</span></Modal.Header>
        <Modal.Body>
          Are you sure you want to exit the session?
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              onLeave();
            }}
            style={{
              backgroundColor: colors.pink500,
              border: "none",
              borderRadius: "20px",
              fontSize: "16px",
              }}
          >
            {exitButtonText}
          </Button>
          <Button onClick={handleClose}
                  style={{
                    backgroundColor: "white",
                    border: `1px solid ${colors.nightlyWoods500}`,
                    borderRadius: "20px",
                    color: colors.nightlyWoods500,
                    fontSize: "16px",
                  }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
